import * as React from "react";
import { Button, Text, Textarea, makeStyles } from "@fluentui/react-components";
import { renderFetchXml } from "./buildFetchXml.js";
import type { FetchXmlBuilderTree } from "./types.js";

export interface QueryDisplayControlProps {
  tree: FetchXmlBuilderTree | null;
}

const useStyles = makeStyles({
  card: {
    border: "1px solid var(--colorNeutralStroke2)",
    borderRadius: "8px",
    padding: "12px",
    display: "grid",
    gap: "8px",
  },
  row: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  textarea: {
    minHeight: "220px",
  },
  errorText: {
    color: "var(--colorPaletteRedForeground1)",
  },
});

export function QueryDisplayControl({ tree }: QueryDisplayControlProps): JSX.Element {
  const styles = useStyles();

  const { xml, error } = React.useMemo(() => {
    if (!tree) {
      return { xml: "", error: "" };
    }

    try {
      return { xml: renderFetchXml(tree), error: "" };
    } catch (buildError) {
      return { xml: "", error: String(buildError) };
    }
  }, [tree]);

  const onCopy = React.useCallback(async () => {
    if (!xml) {
      return;
    }
    await navigator.clipboard.writeText(xml);
  }, [xml]);

  return (
    <div className={styles.card}>
      <Text weight="semibold">Query XML</Text>
      {error ? <Text className={styles.errorText}>{error}</Text> : null}
      <Textarea className={styles.textarea} value={xml} readOnly />
      <div className={styles.row}>
        <Button appearance="secondary" onClick={onCopy} disabled={!xml}>
          Copy XML
        </Button>
      </div>
    </div>
  );
}
